"use client";

import type React from "react";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { onAuthStateChanged, User as FirebaseUser } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";
import { ensureClientAuth } from "@/lib/domain/player/services/client-auth-sync.service";
import type { UserProfileData } from "@/types/user";
import {
  saveProfileToCache,
  clearProfileCache,
} from "@/lib/domain/player/services/user-profile-cache.service";
import type { AppUser } from "@/types/user";

// Your simplified user object for the rest of the app
// No longer includes getIdToken method itself

type AuthContextType = {
  user: AppUser; // Uses the simplified AppUser type
  userProfile: UserProfileData | null;
  loading: boolean;
  isConfigured: boolean;
  isAnonymous: boolean;
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>; // This function will use the internal firebaseUser
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  userProfile: null,
  loading: true,
  isConfigured: false,
  isAnonymous: false,
  getIdToken: (forceRefresh?: boolean) => Promise.resolve(null),
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [appUser, setAppUser] = useState<AppUser>(null); // State for your simplified user object
  const [userProfile, setUserProfile] = useState<UserProfileData | null>(null);
  const [firebaseUserInternal, setFirebaseUserInternal] =
    useState<FirebaseUser | null>(null); // State for the actual Firebase user object
  const [loading, setLoading] = useState(true);
  const [isConfigured, setIsConfigured] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [hasAttemptedRestore, setHasAttemptedRestore] = useState(false);

  useEffect(() => {
    if (!auth) {
      console.warn("Firebase auth is not configured or not initialized yet");
      setLoading(false);
      setIsConfigured(false);
      return;
    }
    setIsConfigured(true);

    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      console.log(
        "AuthProvider: Auth state changed:",
        fbUser
          ? `User: ${fbUser.uid}, Anonymous: ${fbUser.isAnonymous}`
          : "No user"
      );
      setFirebaseUserInternal(fbUser); // Store the raw Firebase user
      if (fbUser) {
        let role = "user";
        let subscriptionTier: "free" | "member" = "free";
        try {
          if (db) {
            // Retry logic to handle race condition where auth token hasn't propagated to Firestore yet
            let userDoc = null;
            let retryCount = 0;
            const maxRetries = 3;
            const retryDelay = 500; // ms

            while (retryCount < maxRetries) {
              try {
                userDoc = await getDoc(doc(db, "users", fbUser.uid));
                break; // Success, exit retry loop
              } catch (firestoreError: unknown) {
                const errorCode = (firestoreError as { code?: string })?.code;
                if (
                  errorCode === "permission-denied" &&
                  retryCount < maxRetries - 1
                ) {
                  console.log(
                    `AuthProvider: Firestore permission denied, retrying in ${retryDelay}ms (attempt ${
                      retryCount + 1
                    }/${maxRetries})`
                  );
                  await new Promise((resolve) =>
                    setTimeout(resolve, retryDelay)
                  );
                  retryCount++;
                } else {
                  throw firestoreError; // Re-throw if not permission-denied or out of retries
                }
              }
            }

            if (userDoc && userDoc.exists()) {
              const userData = userDoc.data();
              role = userData.role || "user";
              // Migrate legacy tier values (player/pro) to member
              const rawTier = userData.subscriptionTier || "free";
              subscriptionTier = rawTier === "free" ? "free" : "member";
              console.log(
                "[AuthProvider] Fetched user data - subscriptionTier:",
                subscriptionTier,
                "from user doc:",
                userData.subscriptionTier
              );
              setUserProfile(userData as UserProfileData);

              // Cache profile data for nav components
              saveProfileToCache({
                id: fbUser.uid,
                displayName: userData.displayName || fbUser.displayName || null,
                photoURL: userData.photoURL || fbUser.photoURL || null,
                email: userData.email || fbUser.email || null,
                handicap: userData.handicap ?? null,
                homeCourseName: userData.homeCourseName || null,
                subscriptionTier: subscriptionTier,
                location: userData.location || null,
              });
            } else {
              setUserProfile(null);
            }
          }
        } catch (error) {
          console.error("Error fetching user role:", error);
          setUserProfile(null);
        }

        setAppUser({
          // Set your simplified user object for the app to consume
          id: fbUser.uid,
          name: fbUser.displayName,
          email: fbUser.email,
          photoURL: fbUser.photoURL,
          isAnonymous: fbUser.isAnonymous,
          role: role,
          subscriptionTier: subscriptionTier,
        });
        setIsAnonymous(fbUser.isAnonymous);

        // Ensure server-side session exists
        try {
          await ensureClientAuth();
        } catch (error) {
          console.error(
            "AuthProvider: Failed to sync auth with server:",
            error
          );
        }
      } else {
        // No Firebase user - try to restore from session cookie if we haven't already
        if (!hasAttemptedRestore) {
          setHasAttemptedRestore(true);
          console.log(
            "AuthProvider: No Firebase user, attempting session restore..."
          );

          try {
            const restored = await ensureClientAuth();
            if (restored) {
              console.log(
                "AuthProvider: Session restore initiated, waiting for auth state update"
              );
              // Don't set loading to false yet - onAuthStateChanged will fire again
              return;
            }
          } catch (restoreError) {
            console.error(
              "AuthProvider: Session restore failed:",
              restoreError
            );
          }
        }

        // No user and restoration failed/skipped - clear state
        setAppUser(null);
        setUserProfile(null);
        setIsAnonymous(false);
        clearProfileCache();
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // getIdToken now uses the internal firebaseUserInternal state
  const getIdToken = useCallback(
    async (forceRefresh = false): Promise<string | null> => {
      if (firebaseUserInternal) {
        try {
          const token = await firebaseUserInternal.getIdToken(forceRefresh);
          // console.log(
          //   "AuthProvider: Token retrieved successfully, length:",
          //   token.length
          // );

          // // Decode the token payload to inspect (for debugging)
          // try {
          //   const payload = JSON.parse(atob(token.split(".")[1]));
          //   console.log("Token payload - aud (audience):", payload.aud);
          //   console.log("Token payload - iss (issuer):", payload.iss);
          //   console.log(
          //     "Token payload - exp:",
          //     payload.exp,
          //     "Current time:",
          //     Math.floor(Date.now() / 1000)
          //   );
          // } catch (e) {
          //   console.log("Could not decode token payload for debugging");
          // }

          return token;
        } catch (error) {
          console.error("AuthProvider: Error getting ID token:", error);
          // Potentially sign out user or handle error appropriately
          return null;
        }
      }
      // console.log("AuthProvider: No firebaseUserInternal to get token from.");
      return null;
    },
    [firebaseUserInternal]
  ); // Depends on the actual Firebase user object

  return (
    <AuthContext.Provider
      value={{
        user: appUser,
        userProfile,
        loading,
        isConfigured,
        isAnonymous,
        getIdToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
